import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseValidationComponent } from '@shared/components/ph-input/components/base-validation.component';
import { PhRadioComponent } from '@shared/components/ph-input/components/ph-radio/ph-radio.component';
import { StorageService } from '@shared/services/storage.service';
import { PhInputComponentInterface } from './ph-input.interface';
import { BehaviorSubject, Subject } from 'rxjs';

@Component({
  selector: 'app-ph-input',
  templateUrl: './ph-input.component.html',
  styleUrls: ['./ph-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PhInputComponent),
    },
  ],
})
export class PhInputComponent
  extends BaseValidationComponent
  implements PhInputComponentInterface, AfterContentInit, AfterViewInit, ControlValueAccessor
{
  @Input() disableDatepickerIcon: boolean = false;
  @Input() showRequired: boolean = false;
  @Input() icon: string = null;
  @Input() min;
  @Input() isWhite: boolean;
  @Input() placeholder: string;
  @Input() searchable: boolean = true;
  @Input() closeOnSelect: boolean = true;
  @Input() errorDescription: boolean = false;
  @Input() autocomplete: boolean = false;
  @Input() disabled: boolean = false;
  @Input() showControl: boolean = true;
  @Input() autoFocus: boolean = false;
  @Input() formControl: UntypedFormControl;
  @Output() keyUpEnter: EventEmitter<any> = new EventEmitter();
  @Output() valueChanged: EventEmitter<any> = new EventEmitter();
  // for select
  @Input() isLanguage: boolean = false;
  @Input() bindLabel: string;
  @Input() selectArray: any[] = [];
  @Input() selectMultiple: boolean = false;
  @Input() bindValue: string;
  @Input() clearable: boolean = true;
  @Input() maxSelectedItems: number = 100;
  @Input() dropdownPosition: 'bottom' | 'top' | 'auto' = 'top';

  // for datepicker
  @Input() datepickerReadOnly: boolean = false;
  @Input() extraOptions: object;
  @Input() onChanged: any;

  @Input() selectCustomTagOption: boolean | any = false;
  @Input() selectCustomTagOptionText: string = 'Add new item: ';
  @ViewChild('inputEl')
  inputEl: ElementRef;

  // for radio
  @ContentChildren(PhRadioComponent)
  phRadio!: QueryList<PhRadioComponent>;
  // for textarea
  @Input() styletype: string = 'default';

  focus: boolean = false;
  labelId: number = Math.random();
  classControl: Subject<string[]> = new BehaviorSubject([]);

  @Output() setAddress: EventEmitter<google.maps.places.PlaceResult | null> = new EventEmitter();
  @Input() googleAutocompleteDefaultValue: string = null;
  @Input() hint: string;
  showHint: boolean = false;

  constructor(public storageService: StorageService, private _cdr: ChangeDetectorRef) {
    super();
  }

  getInputRef(): ElementRef {
    return this.inputEl;
  }

  emitKeyUpEnter() {
    this.keyUpEnter.emit();
  }

  ngAfterContentInit() {
    if (this.type === 'radio') {
      this.initPhRadioGroup();
    }
    this.formControlChanged.subscribe((value: boolean) => {
      this.focus = value;
      this.classControl.next(this.generateClassControl());
    });
  }

  ngAfterViewInit() {
    if (this.autoFocus && this.storageService.get('autofocus')) {
      setTimeout(() => {
        this.focusOnInput();
      });
    }
  }

  focusOnInput() {
    this.toggleFocus(true);
    this.inputEl.nativeElement.focus();
    this.storageService.delete('autofocus');
  }

  get formValidationForInput() {
    return (
      this.formEl.touched && (this.formEl.value !== null ? this.formEl.value.length > 0 : false)
    );
  }

  initPhRadioGroup() {
    this.phRadio.map((radioBtn) =>
      radioBtn.change.subscribe((checkedValue) => this.onChangeRadio(radioBtn, checkedValue))
    );
  }

  uncheckedAllRadio() {
    this.phRadio.map((el) => el.unchecked());
  }

  onChangeRadio(radioBtn, checkedValue) {
    // uncheck radio, without checked
    this.phRadio.map((notCheckedRadio) => {
      if (radioBtn.value !== notCheckedRadio.value) {
        notCheckedRadio.unchecked();
      }
    });
    // choose correct radio
    this.formEl.setValue(checkedValue);
  }

  toggleShowPassword() {
    this.type === 'text' ? (this.type = 'password') : (this.type = 'text');
  }

  toggleFocus(status): void {
    this.focus = status;
  }

  get f() {
    return this.formEl.errors;
  }

  get oneCapitalLetter() {
    return this.f && this.f.oneCapitalLetter;
  }

  get minLength() {
    return this.f && this.f.minlength;
  }

  get minimalOneNumber() {
    return this.f && this.f.oneNumber;
  }

  get email() {
    return this.f && this.f.email;
  }

  updateForm(event) {
    if (!event) {
      this.toggleFocus(event);
    }
    this.formEl.setValue(event);
    this.valueChanged.emit();
  }

  placeChanged(place: google.maps.places.PlaceResult | null): void {
    if (place === null) {
      this.formControlChanged.next(false);
      this.setAddress.emit(null);
    } else {
      this.setAddress.emit(place);
    }
  }

  generateClassControl(): string[] {
    let classControl = [];
    classControl = [
      this.focus || (this.formEl && this.formEl.value) ? 'focus' : '',
      ' ph-input__type-' + this.type,
      this.showRequired ? 'ph-input__show-required' : '',
      this.icon ? 'ph-input__have-icon' : '',
      this.disableDatepickerIcon ? 'noIcon' : '',
    ];
    return classControl;
  }

  writeValue(obj: any): void {}

  registerOnChange(fn: any): void {}

  registerOnTouched(fn: any): void {}
}
